/**
 * Correcting one of the four retained fields.
 *
 * Winnie, 18 September. The rule she settled: don't invent a new correction window, reuse the
 * Accounting Lock Date. An Admin corrects freely while the period is open and is blocked the
 * moment it closes; a Super Admin bypasses the lock. A reason is mandatory for both, and the
 * correction is logged identically for both — "The role restriction narrows *who* can act
 * past a closed period; it never narrows *what* has to be recorded when they do."
 *
 * The arithmetic — the lock test and the materiality test — lives in lib/fieldCorrection so
 * the form, this service and the QA suite share one copy.
 *
 * Why this recalculates when the commission spec says never to: a cost variance means an
 * estimate turned out different from an invoice, and the sale itself did not change. A
 * correction means the recorded figure was wrong from the start. Restating is done by
 * superseding the struck record, never by editing it — see recalculateAfterTermsChange.
 */
import { supabase } from '../lib/supabase'
import { User } from '../types'
import { isSuperAdmin } from '../lib/permissions'
import {
  CorrectableField,
  CorrectorRole,
  FIELD_LABEL,
  assessMateriality,
  downstreamOf,
  isMoneyField,
  isSaleSideField,
  lockVerdict,
} from '../lib/fieldCorrection'
import { loadPlatformDates } from './platformSettingsService'
import { recalculateAfterTermsChange } from './commissionReconService'
import { ACTIONS, logAction } from './actionLog'

export interface FieldCorrection {
  id: string
  dealId: string
  fieldName: CorrectableField
  previousValue: string | null
  correctedValue: string | null
  reason: string
  correctedByName: string
  correctedByRole: CorrectorRole
  bypassedLock: boolean
  flaggedForBasRevision: boolean
  correctedAt: string
}

const toCorrection = (r: Record<string, unknown>): FieldCorrection => ({
  id: String(r.id),
  dealId: String(r.deal_id),
  fieldName: r.field_name as CorrectableField,
  previousValue: (r.previous_value as string) ?? null,
  correctedValue: (r.corrected_value as string) ?? null,
  reason: String(r.reason || ''),
  correctedByName: String(r.corrected_by_name || '—'),
  correctedByRole: (r.corrected_by_role as CorrectorRole) || 'Admin',
  bypassedLock: Boolean(r.bypassed_lock),
  flaggedForBasRevision: Boolean(r.flagged_for_bas_revision),
  correctedAt: String(r.corrected_at || ''),
})

export async function loadCorrections(dealId: string): Promise<FieldCorrection[]> {
  const { data, error } = await supabase
    .from('field_corrections')
    .select('*')
    .eq('deal_id', dealId)
    .order('corrected_at', { ascending: false })
  if (error) throw error
  return ((data || []) as unknown as Record<string, unknown>[]).map(toCorrection)
}

/** The database column each correctable field maps to. */
const COLUMN: Record<CorrectableField, string> = {
  sale_price: 'sale_price',
  sold_date: 'sale_date',
  purchase_price: 'valuation_amount',
  purchase_date: 'acquired_date',
}

export interface CorrectionResult {
  ok: boolean
  message: string
  flaggedForBasRevision?: boolean
}

export async function correctField(args: {
  dealId: string
  field: CorrectableField
  correctedValue: string
  reason: string
  user: User
}): Promise<CorrectionResult> {
  const reason = String(args.reason || '').trim()
  if (!reason) {
    return {
      ok: false,
      message: 'Say why this figure is being corrected. No correction saves without a reason, whatever your role.',
    }
  }

  const role: CorrectorRole = isSuperAdmin(args.user) ? 'SuperAdmin' : 'Admin'

  const [dates, dealRes] = await Promise.all([
    loadPlatformDates(),
    supabase
      .from('deals')
      .select('id, autograb_vin, vehicle_vin, sale_price, sale_date, valuation_amount, acquired_date, invoice_id')
      .eq('id', args.dealId)
      .maybeSingle(),
  ])
  if (dealRes.error) throw dealRes.error
  const deal = dealRes.data as Record<string, unknown> | null
  if (!deal) return { ok: false, message: 'That vehicle could not be loaded.' }

  /* Which date decides the period: the sold date for sale-side fields, the purchase date for
     purchase-side ones. Winnie is explicit about the pairing. */
  const effectiveDate = isSaleSideField(args.field)
    ? (deal.sale_date as string) || null
    : (deal.acquired_date as string) || null

  const verdict = lockVerdict({
    field: args.field,
    effectiveDate,
    accountingLockDate: dates.accountingLockDate,
    role,
  })
  if (!verdict.allowed) return { ok: false, message: verdict.message }

  const column = COLUMN[args.field]
  const previousRaw = deal[column]
  const previousValue = previousRaw == null ? '' : String(previousRaw)

  const correctedValue = String(args.correctedValue || '').trim()
  if (!correctedValue) return { ok: false, message: 'Enter the corrected value.' }
  if (isMoneyField(args.field) && !Number.isFinite(Number(correctedValue))) {
    return { ok: false, message: 'That is not a number.' }
  }
  if (previousValue === correctedValue) {
    return { ok: false, message: 'That is the figure already recorded — nothing to correct.' }
  }

  /* Materiality is only ever asked about a bypassed-lock correction: an ordinary Admin
     correction can only touch an open period, which has not been lodged. */
  const materiality = assessMateriality(args.field, previousRaw, correctedValue)
  const flagged = verdict.bypassedLock && materiality.material

  const { error: insErr } = await supabase.from('field_corrections').insert([
    {
      deal_id: args.dealId,
      vin: String(deal.autograb_vin || deal.vehicle_vin || ''),
      field_name: args.field,
      previous_value: previousValue,
      corrected_value: correctedValue,
      reason,
      corrected_by: args.user?.id || null,
      corrected_by_name: args.user?.full_name || args.user?.email || null,
      corrected_by_role: role,
      bypassed_lock: verdict.bypassedLock,
      flagged_for_bas_revision: flagged,
      variance_pct: materiality.variancePct,
      variance_dollar: materiality.varianceDollar,
    },
  ])
  if (insErr) throw insErr

  const { error: updErr } = await supabase
    .from('deals')
    .update({ [column]: isMoneyField(args.field) ? Number(correctedValue) : correctedValue })
    .eq('id', args.dealId)
  if (updErr) throw updErr

  const notes: string[] = []
  const downstream = downstreamOf(args.field)

  if (downstream.commission) {
    try {
      const r = await recalculateAfterTermsChange(
        args.dealId,
        FIELD_LABEL[args.field] + ' corrected: ' + reason,
        args.user?.id,
      )
      if (r.superseded) notes.push(r.message)
      else if (!r.ok) notes.push(r.message)
    } catch (e) {
      notes.push('commission could not be restated (' + (e instanceof Error ? e.message : 'unknown') + ')')
    }
  }

  if (downstream.arAmountDue && deal.invoice_id) {
    const { error } = await supabase
      .from('invoices')
      .update({ total_amount: Number(correctedValue) })
      .eq('id', String(deal.invoice_id))
    if (error) notes.push('the receivable could not be updated')
  }

  /* The margin scheme credit and G1 are derived on every read from the sale price, the
     purchase price and the sold date. Correcting any of those is therefore the whole
     recalculation — there is no stored figure to rewrite, which is why nothing is written
     here and why the register will already be right the next time it is opened. */

  await logAction({
    dealId: args.dealId,
    action: ACTIONS.FIELD_CORRECTED,
    actorId: args.user?.id || null,
    details: {
      field: args.field,
      previous_value: previousValue,
      corrected_value: correctedValue,
      reason,
      role,
      bypassed_lock: verdict.bypassedLock,
      flagged_for_bas_revision: flagged,
    },
  })

  const basNote = flagged
    ? ' This correction moves a figure that has already been lodged on a BAS by more than the materiality ' +
      'threshold — it is flagged for the finance team to pick up as a manual adjustment on the next BAS ' +
      'lodgement in Xero. The platform does not amend the historical BAS.'
    : verdict.bypassedLock
      ? ' The period was already closed, so this is recorded as a Super Admin override. The variance is below the ' +
        'materiality threshold and is absorbed.'
      : ''

  return {
    ok: true,
    flaggedForBasRevision: flagged,
    message: FIELD_LABEL[args.field] + ' corrected.' + (notes.length ? ' ' + notes.join(' ') : '') + basNote,
  }
}
